/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CompanyDataHero.module.scss';

type Mode = 'off' | 'on';

type LedgerRow = {
  field: string;
  source: string;
  filing: string;
  declined?: boolean;
};

const LEDGER_ROWS: LedgerRow[] = [
  { field: 'Company status · Active', source: 'Companies House, GB', filing: 'CS01 · 2026-03-06' },
  { field: 'Incorporation · 28 Jan 2015', source: 'Companies House, GB', filing: 'IN01 · 2015-01-28' },
  { field: 'PSCs · 2 individuals', source: 'PSC register, GB', filing: 'PSC · 2026-03-06' },
  { field: 'Turnover · not published', source: 'Companies House, GB', filing: 'AA · 2026-01-31', declined: true },
];

const AUTO_SWITCH_DELAY = 2400;
const ROW_STAGGER_DELAY = 260;

const isoStamp = () => {
  const d = new Date();
  const p = (n: number) => (n < 10 ? `0${n}` : `${n}`);

  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(
    d.getUTCMinutes(),
  )}:${p(d.getUTCSeconds())}Z`;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const CompanyDataHero = () => {
  const { t } = useInt();
  const [mode, setMode] = useState<Mode>('off');
  const [stamp, setStamp] = useState('model recall');
  const [visibleRows, setVisibleRows] = useState(0);
  const userTouchedRef = useRef(false);
  const tokenRef = useRef(0);

  const applyMode = (nextMode: Mode, animate: boolean) => {
    const token = (tokenRef.current += 1);

    setMode(nextMode);

    if (nextMode === 'off') {
      setStamp('model recall');
      setVisibleRows(0);
      return;
    }

    setStamp(`retrieved ${isoStamp()}`);

    if (!animate || prefersReducedMotion()) {
      setVisibleRows(LEDGER_ROWS.length);
      return;
    }

    setVisibleRows(0);
    LEDGER_ROWS.forEach((_, i) => {
      setTimeout(
        () => {
          if (tokenRef.current === token) {
            setVisibleRows(i + 1);
          }
        },
        (i + 1) * ROW_STAGGER_DELAY,
      );
    });
  };

  const handleSelect = (nextMode: Mode) => {
    userTouchedRef.current = true;
    applyMode(nextMode, true);
  };

  useEffect(() => {
    if (prefersReducedMotion()) {
      applyMode('on', false);
      return undefined;
    }

    const id = setTimeout(() => {
      if (!userTouchedRef.current) {
        applyMode('on', true);
      }
    }, AUTO_SWITCH_DELAY);

    return () => clearTimeout(id);
  }, []);

  return (
    <Section className={styles.root}>
      <TitleBlock
        size="page"
        title={t('Financials, UBOs and group structure your AI agents can cite.')}
        description={
          <>
            {t(
              'Company data for AI agents, read from official government registries. Every value carries the register it came from, the filing reference and the time of the read, across',
            )}{' '}
            <strong>{t('600M+ companies')}</strong> {t('in 200+ countries.')}
          </>
        }
      />

      <div className={styles.actions}>
        <Button type="blue" size="xxl" rounded="full" href="/data-dictionary">
          {t('View data dictionary')}
        </Button>
        <Button type="dark-outline" size="xxl" rounded="full" href="/mcp-integration">
          {t('Connect the MCP server')}
        </Button>
      </div>

      <div className={styles.panelWrap}>
        <div className={styles.glowBlue} aria-hidden="true" />
        <div className={styles.glowIndigo} aria-hidden="true" />

        <div className={styles.panel}>
          <div className={styles.panelTop}>
            <div className={styles.seg} role="group" aria-label="Answer mode">
              <button
                type="button"
                className={cn(styles.segBtn, mode === 'off' && styles.segBtnActive)}
                aria-pressed={mode === 'off'}
                onClick={() => handleSelect('off')}
              >
                {t('Without retrieval')}
              </button>
              <button
                type="button"
                className={cn(styles.segBtn, mode === 'on' && styles.segBtnActive)}
                aria-pressed={mode === 'on'}
                onClick={() => handleSelect('on')}
              >
                {t('With retrieval')}
              </button>
            </div>
            <span className={styles.stamp}>{stamp}</span>
          </div>

          <div className={styles.panelQ}>
            <span className={styles.avQ} aria-hidden="true">
              Q
            </span>
            <span>
              Is Global Data Intelligence Limited active, who controls it, and what was its last reported turnover?
            </span>
          </div>

          <div className={styles.states}>
            <div className={cn(styles.state, mode === 'off' && styles.stateOn)}>
              <div className={styles.panelA}>
                <span className={styles.avA} aria-hidden="true">
                  <span className={styles.avADot} />
                </span>
                <div className={styles.answer}>
                  Global Data Intelligence Limited is an active UK company based in Northampton. It is a privately held
                  business intelligence provider, majority owned by its founder, and its most recent accounts show
                  turnover of around £4.2 million.
                </div>
              </div>
              <div className={styles.panelFoot}>
                <span className={cn(styles.pill, styles.pillBad)}>0 sources</span>
                <span className={styles.pill}>No timestamp</span>
                <span className={styles.pill}>No filing reference</span>
                <span className={styles.pill}>Cannot be reproduced</span>
              </div>
            </div>

            <div className={cn(styles.state, mode === 'on' && styles.stateOn)}>
              <div className={styles.panelA}>
                <span className={styles.avA} aria-hidden="true">
                  <span className={styles.avADot} />
                </span>
                <div className={styles.answer}>
                  Active on the UK register.<sup>1</sup> Incorporated{' '}
                  <span className={styles.mono}>28 January 2015</span>, registered office{' '}
                  <span className={styles.mono}>Artisans&apos; House, 7 Queensbridge, Northampton NN4 7BF</span>.
                  <sup>2</sup> Two people with significant control are recorded, both natural persons.<sup>3</sup>{' '}
                  Turnover is not published: the accounts on file do not include a profit and loss account.
                  <sup>4</sup>
                </div>
              </div>

              <div className={styles.ledger}>
                {LEDGER_ROWS.map((row, i) => (
                  <div
                    key={row.field}
                    className={cn(
                      styles.ledgerRow,
                      row.declined && styles.ledgerRowNa,
                      i < visibleRows && styles.ledgerRowIn,
                    )}
                  >
                    <span className={styles.ledgerIndex}>{i + 1}</span>
                    <span className={styles.ledgerField}>{row.field}</span>
                    <span className={styles.ledgerSource}>
                      <Icon url="/storage/flags/gb.svg" className={styles.flag} />
                      {row.source}
                    </span>
                    <span className={styles.ledgerFiling}>{row.filing}</span>
                  </div>
                ))}
              </div>

              <div className={styles.panelFoot}>
                <span className={cn(styles.pill, styles.pillOk)}>3 values, 3 filings</span>
                <span className={cn(styles.pill, styles.pillBad)}>1 declined, reason given</span>
                <span className={styles.pill}>Reproducible on re-query</span>
              </div>
            </div>
          </div>

          <div className={styles.verifyBar}>
            <span>{t('Every value above is on the public register. Check it yourself:')}</span>
            <a
              href="https://find-and-update.company-information.service.gov.uk/company/09410808"
              rel="noopener noreferrer"
              target="_blank"
            >
              Companies House · 09410808
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M4.5 2.5h5v5M9.5 2.5 3 9" />
              </svg>
            </a>
          </div>
        </div>

        <p className={styles.panelNote}>
          {t(
            'Same question, same company: our own. The left answer reads well and cannot be checked. The right one declines the field the register does not publish.',
          )}
        </p>
      </div>
    </Section>
  );
};
